import React from 'react';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { CaretRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard } from '../../../../../components/glass';
import type { IContaCilResumo } from '../../../../../hooks/useContaCilDashboard';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const C_FECHADA = '#0EA371';
const C_ANDAMENTO = '#6D5EF0';
const C_FORA = '#E0524D';

interface Seg {
  label: string;
  valor: number;
  cor: string;
}

const Donut = ({ segs, total, centro }: { segs: Seg[]; total: number; centro: number }) => {
  const base = Math.max(1, total);
  let acc = 0;
  return (
    <Box position="relative" w="128px" h="128px" flexShrink={0}>
      <svg width="128" height="128" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r="54" fill="none" stroke="#EEF0F6" strokeWidth="15" />
        {segs.map(s => {
          const pct = (s.valor / base) * 100;
          const el = (
            <circle
              key={s.label}
              cx="64"
              cy="64"
              r="54"
              fill="none"
              stroke={s.cor}
              strokeWidth="15"
              pathLength={100}
              strokeDasharray={`${pct} ${100 - pct}`}
              strokeDashoffset={-acc}
              transform="rotate(-90 64 64)"
              strokeLinecap="butt"
            />
          );
          acc += pct;
          return el;
        })}
      </svg>
      <Flex
        position="absolute"
        inset={0}
        direction="column"
        align="center"
        justify="center"
        pointerEvents="none"
      >
        <Text
          fontSize="26px"
          fontWeight="700"
          color="#0F172A"
          lineHeight="1"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatNumber(centro)}
        </Text>
        <Text fontSize="10px" color="#94A3B8" fontWeight="600">
          fechadas
        </Text>
      </Flex>
    </Box>
  );
};

export const EmpresasPanel = ({ data }: { data?: IContaCilResumo }) => {
  const navigate = useNavigate();
  const base = data?.emp_base ?? 0;
  const fechadas = data?.emp_fechadas ?? 0;
  const andamento = data?.emp_andamento ?? 0;
  const fora = data?.emp_fora ?? 0;
  const foraLista = data?.fora_lista ?? [];
  const segs: Seg[] = [
    { label: 'Fechadas · Domínio', valor: fechadas, cor: C_FECHADA },
    { label: 'Em andamento', valor: andamento, cor: C_ANDAMENTO },
    { label: 'Fora de operação', valor: fora, cor: C_FORA },
  ];
  const pct = (v: number) => (base > 0 ? ((v / base) * 100).toFixed(1).replace('.', ',') : '0,0');

  return (
    <Stack spacing={4} h="full">
      {/* Donut */}
      <GlassCard intensity="medium" p={5}>
        <Flex justify="space-between" align="baseline" mb={4}>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Empresas no ContaCil
          </Text>
          <Text
            fontSize="20px"
            fontWeight="700"
            color="#0F172A"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatNumber(base)}
          </Text>
        </Flex>
        <Flex align="center" gap={5}>
          <Donut segs={segs} total={base} centro={fechadas} />
          <Stack spacing={2.5} flex={1}>
            {segs.map(s => (
              <Flex key={s.label} align="center" gap={2}>
                <Box w="9px" h="9px" borderRadius="3px" bg={s.cor} flexShrink={0} />
                <Text fontSize="11.5px" color="#64748B" flex={1} noOfLines={1}>
                  {s.label}
                </Text>
                <Text
                  fontSize="12px"
                  fontWeight="700"
                  color="#0F172A"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatNumber(s.valor)}
                </Text>
                <Text fontSize="10px" color="#94A3B8" w="38px" textAlign="right">
                  {pct(s.valor)}%
                </Text>
              </Flex>
            ))}
          </Stack>
        </Flex>
        <Text fontSize="10px" color="#94A3B8" mt={3} fontFamily="mono">
          fonte: tabela de fechamento · registrado ao exportar p/ Domínio
        </Text>
      </GlassCard>

      {/* Lista fora de operação */}
      <GlassCard intensity="medium" p={5} flex={1}>
        <Flex justify="space-between" align="baseline" mb={3}>
          <Box>
            <Text
              fontSize="9.5px"
              fontWeight="600"
              color="#94A3B8"
              textTransform="uppercase"
              letterSpacing="0.06em"
            >
              Sem nenhum arquivo
            </Text>
            <Text fontSize="14px" fontWeight="700" color="#0F172A">
              Empresas fora de operação
            </Text>
          </Box>
          <Text
            fontSize="12px"
            fontWeight="700"
            color={C_FORA}
            bg={`${C_FORA}14`}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            {formatNumber(fora)}
          </Text>
        </Flex>
        {foraLista.length === 0 ? (
          <Text fontSize="12px" color="#94A3B8" py={4} textAlign="center">
            Todas as empresas operaram no período. 🎉
          </Text>
        ) : (
          <Stack spacing={0}>
            {foraLista.map(e => (
              <Flex
                key={e.empresa_id}
                as="button"
                align="center"
                gap={2}
                py={2}
                borderBottomWidth="1px"
                borderColor="rgba(15,23,42,0.05)"
                onClick={() =>
                  navigate(
                    `/modulesHome/accountingConciliation/conciliacao-contabil/empresas?empresa=${e.empresa_id}`,
                  )
                }
                textAlign="left"
                w="full"
              >
                <Box w="6px" h="6px" borderRadius="full" bg={C_FORA} flexShrink={0} />
                <Box flex={1} minW={0}>
                  <Text fontSize="12.5px" fontWeight="600" color="#0F172A" noOfLines={1}>
                    {e.razao_social || `Empresa ${e.empresa_id}`}
                  </Text>
                  {e.uf && (
                    <Text fontSize="10px" color="#94A3B8">
                      {e.uf}
                    </Text>
                  )}
                </Box>
                <Text fontSize="11px" color="#64748B" flexShrink={0}>
                  {e.contas} {e.contas === 1 ? 'conta' : 'contas'}
                </Text>
              </Flex>
            ))}
          </Stack>
        )}
        {fora > foraLista.length && (
          <Flex
            as="button"
            align="center"
            justify="center"
            gap={1}
            mt={3}
            w="full"
            color="#7C3AED"
            onClick={() =>
              navigate('/modulesHome/accountingConciliation/conciliacao-contabil/empresas')
            }
          >
            <Text fontSize="11px" fontWeight="600">
              Ver todas as {formatNumber(fora)} empresas
            </Text>
            <CaretRight size={12} weight="bold" />
          </Flex>
        )}
      </GlassCard>
    </Stack>
  );
};
